import { Form, Button, Table, Modal } from "react-bootstrap";
import XButton from "../../../component/button/XButton";
import PaginationNav from "../../../component/pagination/pagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faCircleXmark,
  faEye,
  faMagnifyingGlass,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api";
import useAxios from "../../../hooks/useAxios";
import useSidebar from "../../../hooks/useSidebar";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toaster } from "../../../utils/toast";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/rootReducer";
import { getTagsRequest } from "../../../redux/modules/contacts/contactSlice";
import XLoader from "../../../component/loader/XLoader";
import XDeleteModal from "../../../component/modal/XDeleteModal";

const Tags = () => {
  const { postData } = useAxios();
  const [selectedTagId, setSelectedTagId] = useState("");
  const { onShowSidebar } = useSidebar();
  const [addTagsModalShow, setAddTagsModalShow] = useState(false);
  const navigate = useNavigate();
  const {
    tags: { tags, totalCount },
    loading,
  } = useSelector((state: RootState) => state.contactlist);
  const { selectedLocationId } = useSelector((state: RootState) => state.user);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [queryParams, setQueryParams] = useState({
    search: "",
    page: 1,
    limit: 10,
  });
  const dispatch = useDispatch();
  const [showDeleteTagModal, setShowDeleteTagModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState<any>("");

  const validationSchema = Yup.object().shape({
    tagName: Yup.string()
      .required("Tag name is required")
      .test((value) => value.trim() !== ""),
  });

  const formik = useFormik({
    initialValues: {
      tagName: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const payload = selectedTagId
        ? { ...values, tagId: selectedTagId }
        : values;

      const response = await postData(
        API_ENDPOINTS.CREATE_OR_FIND_TAG,
        payload
      );

      const { success, message } = response;

      if (success) {
        setSelectedTagId("");
        formik.resetForm();
        setAddTagsModalShow(false);
        dispatch(getTagsRequest(queryParams));
      }
      toaster({ message, success });
    },
  });

  useEffect(() => {
    dispatch(getTagsRequest(queryParams));
    const totalPages = Math.ceil(totalCount / queryParams?.limit);
    setTotalPages(totalPages);
  }, [queryParams, totalCount, selectedLocationId]);

  const handleEditClick = async (tagId: any) => {
    const existTag = tags?.find((tag: any) => tag.id === tagId) as any;
    setSelectedTagId(tagId);
    formik.setFieldValue("tagName", existTag?.name);
    setAddTagsModalShow(true);
  };

  const handleDeleteIconClick = async (tag: any) => {
    setShowDeleteTagModal(true);
    setSelectedTag(tag);
  };

  const handleDeleteTag = async (tagId: any) => {
    const response = await postData(`${API_ENDPOINTS.DELETE_TAG}/${tagId}`);
    if (response.success) {
      toaster({ message: response.message, success: response.success });
      dispatch(getTagsRequest(queryParams));
    }
  };

  const handleQueryChange = (key: string, value: string | number) => {
    setQueryParams((prevParams) => ({ ...prevParams, [key]: value }));
  };

  return (
    <div>
      <div className="main-title d-flex align-items-center justify-content-between main-title-block">
        <h2 className="d-flex align-items-center">
          <span
            className="d-lg-none cursor-pointer"
            onClick={() => onShowSidebar()}
          >
            <FontAwesomeIcon icon={faBars} />
          </span>
          Tags
        </h2>
        <div className="filter-bar d-flex align-items-center row-item top-block">
          <div className="position-relative search-bar">
            <Form.Control
              type="search"
              placeholder="Search"
              aria-label="Search"
              value={queryParams.search}
              onChange={(e) => handleQueryChange("search", e.target.value)}
            />
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </div>
          <XButton
            buttonText="Add Tag"
            className="btn-theme"
            onClick={() => {
              setSelectedTagId("");
              setAddTagsModalShow(true);
            }}
          />
        </div>
      </div>

      <div className="table-listing all-contacts users-list position-relative">
        <Table responsive>
          <thead>
            <tr>
              <th>Tags Name</th>
              <th>Assigned Contacts</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {!loading && tags?.length === 0 && (
              <tr>
                <td colSpan={9}>
                  <p className="text-center no-records">No Tags found.</p>
                </td>
              </tr>
            )}
            {!loading &&
              tags?.map((tag: any) => (
                <tr key={tag.id}>
                  <td>{tag?.name}</td>
                  <td>
                    <button
                      type="button"
                      style={{
                        border: "1px solid #00B05C",
                        borderRadius: 30,
                        background: "transparent",
                        color: "#00B05C",
                        padding: "8px 16px",
                        fontSize: 14,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        navigate(
                          `/org/settings/tags/assigned-tag-contacts/${tag.id}`,
                          { state: { tagId: tag.id, tagName: tag.name } }
                        )
                      }
                    >
                      <FontAwesomeIcon icon={faEye} /> View ({tag?.total_contacts ?? 0})
                    </button>
                  </td>
                  <td className="actions">
                    <span>
                      <FontAwesomeIcon
                        icon={faCircleXmark}
                        onClick={() => handleDeleteIconClick(tag)}
                      />
                    </span>
                    <span>
                      <FontAwesomeIcon
                        icon={faPenToSquare}
                        onClick={() => handleEditClick(tag.id)}
                      />
                    </span>
                  </td>
                </tr>
              ))}
            {loading && <XLoader />}
          </tbody>
        </Table>
      </div>

      <Modal
        show={addTagsModalShow}
        onHide={() => {
          setAddTagsModalShow(false);
          formik.resetForm();
        }}
        centered
      >
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{selectedTagId ? "Edit Tag" : "Add Tag"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="form-group" controlId="formBasicEmail">
              <Form.Label>Tag Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter"
                name="tagName"
                value={formik.values.tagName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.tagName && !!formik.errors.tagName}
                required
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.tagName}
              </Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button
              variant="primary"
              className="btn-theme btn-no-border"
              onClick={() => {
                setAddTagsModalShow(false);
                formik.resetForm();
              }}
            >
              Close
            </Button>
            <XButton variant="primary" type="submit" buttonText={<>Submit</>} />
          </Modal.Footer>
        </Form>
      </Modal>

      <XDeleteModal
        show={showDeleteTagModal}
        handleClose={() => setShowDeleteTagModal(false)}
        title="Delete Tag"
        body={
          <p>
            Are you sure you want to delete the
            <span style={{ fontWeight: "bold" }}> {selectedTag?.name}</span> Tag?
            <p>If you delete this tag, it will be removed from all contacts associated with it.</p>
          </p>
        }
        onConfirm={() => {
          if (selectedTag) {
            handleDeleteTag(selectedTag.id);
          }
          setShowDeleteTagModal(false);
        }}
        onCancel={() => setShowDeleteTagModal(false)}
      />

      <PaginationNav
        totalPages={totalPages}
        currentPage={queryParams.page}
        handlePageChange={(page) => handleQueryChange("page", page)}
      />
    </div>
  );
};

export default Tags;
